// unsignned user
import fetchIsGoalGTP from '@api/fetchers/constants';
import { fetchCatalogueTypes } from '@api/fetchers/doubts-and-solutions';
import { fetchGoalInfo } from '@api/fetchers/goal';
import { useSetSelectedNav } from '@stores/sidenav';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';
import { SWRConfig } from 'swr';
import Layout from '@layout/index';
import CatalogueType from '@cont/CatalogueType/CatalogueType';
import {
  useBookCatalogue,
  usePYQCatalogue
} from '@api/hooks/doubts-and-solutions/useCatalogs';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';

const CatalogueTypeHomePage = ({ fallbackData }) => {
  const { title, uid: goalUID } = fallbackData.goalInfo || {};
  const { key: bookCatlogskey } = useBookCatalogue(goalUID);
  const { key: pyqCatlogskey } = usePYQCatalogue(goalUID);
  const { key: goalInfoSWRKey } = useGoalInfo(goalUID);
  const metaTitle = `Solutions | ${title} | Unacademy`;
  useSetSelectedNav('solutions');
  useSubjectAndLanguages(goalUID);

  return (
    <SWRConfig
      value={{
        dedupingInterval: 60 * 1000,
        fallback: {
          [goalInfoSWRKey]: fallbackData.goalInfo,
          [bookCatlogskey]: fallbackData.bookCatlaogues,
          [pyqCatlogskey]: fallbackData.pyqCatalogues
        }
      }}
    >
      <NextSeo
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <CatalogueType goalUID={goalUID} />
    </SWRConfig>
  );
};

export const getStaticProps = async ({ params }) => {
  const { goalSlug, goalUID } = params;

  const isGTP = await fetchIsGoalGTP(goalUID);
  if (isGTP)
    return {
      redirect: {
        destination: `/goal/gtp/${goalSlug}/${goalUID}`,
        permanent: true
      }
    };

  try {
    const goal = await fetchGoalInfo(goalUID);
    if (!goal || goal.errorCode) return { notFound: true, revalidate: 5 };
    const promises = [];

    promises.push(fetchCatalogueTypes(goalUID, 1));
    promises.push(fetchCatalogueTypes(goalUID, 2));

    const [bookCatlaogues, pyqCatalogues] = await Promise.all(promises);

    const fallbackData = {};
    fallbackData.goalInfo = goal;
    fallbackData.bookCatlaogues = bookCatlaogues;
    fallbackData.pyqCatalogues = pyqCatalogues;

    return {
      props: {
        goalUID,
        goalSlug,
        fallbackData
      },
      revalidate: 5
    };
  } catch (error) {
    return { notFound: true, revalidate: 5 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

CatalogueTypeHomePage.pageInfo = {
  isMWebSupported: true,
  name: 'Doubt Solutions Page',
  layoutOptions: {
    hideFreshbot: true,
    hideFooter: false,
    showPlusNav: true
  }
};
CatalogueTypeHomePage.Layout = Layout;

export default CatalogueTypeHomePage;
